/**
 * React Query Client Configuration
 * Centralized query keys, stale time configs, and mutation helpers
 */

// =============================================================================
// STALE TIME CONFIGURATION
// =============================================================================

export const staleTimeConfig = {
  // Real-time data (1 second)
  realTime: 1000,

  // Market data (5 seconds)
  marketData: 5 * 1000,

  // Orders (10 seconds)
  orders: 10 * 1000,

  // Positions (30 seconds)
  positions: 30 * 1000,

  // Portfolio (1 minute)
  portfolio: 60 * 1000,

  // Performance data (5 minutes)
  performance: 5 * 60 * 1000,

  // Risk data (5 minutes)
  risk: 5 * 60 * 1000,

  // Analytics (10 minutes)
  analytics: 10 * 60 * 1000,

  // Symbols/static data (1 hour)
  symbols: 60 * 60 * 1000,

  // User settings (30 minutes)
  userSettings: 30 * 60 * 1000,
} as const;

// =============================================================================
// QUERY KEYS
// =============================================================================

export const queryKeys = {
  // Market Data
  marketData: {
    ticker: (symbol: string) => ['market-data', 'ticker', symbol] as const,
    tickerList: (symbols: string[]) => ['market-data', 'ticker-list', symbols] as const,
    symbolSearch: (query: string) => ['market-data', 'symbol-search', query] as const,
    ohlcv: {
      data: (symbol: string, timeframe: string, params?: any) =>
        ['market-data', 'ohlcv', symbol, timeframe, params] as const,
    },
    orderBook: (symbol: string, depth?: number) =>
      ['market-data', 'order-book', symbol, depth] as const,
    quotes: {
      current: (symbol: string) => ['market-data', 'quotes', 'current', symbol] as const,
      batch: (symbols: string[]) => ['market-data', 'quotes', 'batch', symbols] as const,
    },
    indicators: {
      data: (symbol: string, indicator: string, params?: any) =>
        ['market-data', 'indicators', symbol, indicator, params] as const,
    },
    dataSources: () => ['market-data', 'data-sources'] as const,
  },

  // Trading
  trading: {
    all: ['trading'] as const,
    account: () => ['trading', 'account'] as const,
    orders: {
      all: () => ['trading', 'orders'] as const,
      list: (filters?: any) => ['trading', 'orders', 'list', filters] as const,
      detail: (orderId: string) => ['trading', 'orders', orderId] as const,
      history: (orderId?: string, filters?: any) =>
        ['trading', 'orders', 'history', orderId, filters] as const,
    },
    positions: {
      all: () => ['trading', 'positions'] as const,
      detail: (symbol: string) => ['trading', 'positions', symbol] as const,
    },
  },

  // Portfolio
  portfolio: {
    all: ['portfolio'] as const,
    overview: (portfolioId?: string) => ['portfolio', 'overview', portfolioId] as const,
    positions: (portfolioId?: string) => ['portfolio', 'positions', portfolioId] as const,
    performance: (portfolioId?: string, timePeriod?: string) =>
      ['portfolio', 'performance', portfolioId, timePeriod] as const,
    risk: (portfolioId?: string, timePeriod?: string) =>
      ['portfolio', 'risk', portfolioId, timePeriod] as const,
    allocation: (portfolioId?: string) => ['portfolio', 'allocation', portfolioId] as const,
    sectors: (portfolioId?: string) => ['portfolio', 'sectors', portfolioId] as const,
    analytics: (portfolioId?: string, timePeriod?: string) =>
      ['portfolio', 'analytics', portfolioId, timePeriod] as const,
    correlation: (portfolioId?: string, timePeriod?: string) =>
      ['portfolio', 'correlation', portfolioId, timePeriod] as const,
    benchmarks: (benchmarks: string[], timePeriod?: string) =>
      ['portfolio', 'benchmarks', benchmarks, timePeriod] as const,
    comparison: (portfolioId: string | undefined, benchmarks: string[], timePeriod?: string) =>
      ['portfolio', 'comparison', portfolioId, benchmarks, timePeriod] as const,
  },

  // Risk Management
  risk: {
    all: ['risk'] as const,
    limits: (portfolioId?: string) => ['risk', 'limits', portfolioId] as const,
    assessment: (portfolioId?: string) => ['risk', 'assessment', portfolioId] as const,
    alerts: (portfolioId?: string) => ['risk', 'alerts', portfolioId] as const,
  },

  // AI Trading
  aiTrading: {
    all: ['ai-trading'] as const,
    agents: {
      all: () => ['ai-trading', 'agents'] as const,
      detail: (agentId: string) => ['ai-trading', 'agents', agentId] as const,
      performance: (agentId: string, period?: string) => ['ai-trading', 'agents', agentId, 'performance', period] as const,
    },
    signals: (agentId?: string) => ['ai-trading', 'signals', agentId] as const,
    strategies: () => ['ai-trading', 'strategies'] as const,
  },

  // Chat AI
  chat: {
    all: ['chat'] as const,
    conversations: () => ['chat', 'conversations'] as const,
    conversation: (id: string) => ['chat', 'conversations', id] as const,
    messages: (conversationId: string) => ['chat', 'messages', conversationId] as const,
    marketBrief: () => ['chat', 'market-brief'] as const,
    portfolioInsights: () => ['chat', 'portfolio-insights'] as const,
  },

  // Arbitrage
  arbitrage: {
    all: ['arbitrage'] as const,
    opportunities: (filters?: any) => ['arbitrage', 'opportunities', filters] as const,
    executions: (limit?: number) => ['arbitrage', 'executions', limit] as const,
    settings: () => ['arbitrage', 'settings'] as const,
    exchanges: () => ['arbitrage', 'exchanges'] as const,
    performance: (period?: string) => ['arbitrage', 'performance', period] as const,
  },

  // Auth
  auth: {
    user: () => ['auth', 'user'] as const,
    session: () => ['auth', 'session'] as const,
  },
} as const;

// =============================================================================
// MUTATION HELPERS
// =============================================================================

export const mutationHelpers = {
  /**
   * Helper to invalidate related queries after a mutation
   */
  invalidateQueries: (queryClient: any, keys: (string | string[])[]) => {
    keys.forEach(key => {
      queryClient.invalidateQueries({ queryKey: key });
    });
  },

  /**
   * Helper to optimistically update query cache
   */
  optimisticUpdate: <T>(
    queryClient: any,
    queryKey: readonly unknown[],
    updater: (old: T | undefined) => T
  ) => {
    queryClient.setQueryData(queryKey, updater);
  },

  /**
   * Helper to cancel ongoing queries
   */
  cancelQueries: (queryClient: any, keys: (string | string[])[]) => {
    keys.forEach(key => {
      queryClient.cancelQueries({ queryKey: key });
    });
  },
} as const;

